import React, { useState, useRef, useEffect } from 'react';
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  FormGroup,
  Label,
  Input
} from 'reactstrap';
import moment from 'moment';
import './DateFilter.css';

const DateFilter = ({ onDateChange, selectedRange = 'Last 7 days' }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(selectedRange);
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [modalDroppedAlert, setModalDroppedAlert] = useState(false);

  const toggle = () => setDropdownOpen(prevState => !prevState);
  const toggleCustomModal = () => setCustomModalOpen(!customModalOpen);

  const dateOptions = [
    { label: '1 Day', value: '1d', getValue: () => ({
      start: moment().subtract(1, 'days').startOf('day').toDate(),
      end: moment().endOf('day').toDate()
    })},
    { label: 'Last 7 days', value: '7d', getValue: () => ({
      start: moment().subtract(7, 'days').startOf('day').toDate(),
      end: moment().endOf('day').toDate()
    })},
    { label: 'Last 30 days', value: '30d', getValue: () => ({
      start: moment().subtract(30, 'days').startOf('day').toDate(),
      end: moment().endOf('day').toDate()
    })},
    { label: 'Last 4 weeks', value: '4w', getValue: () => ({
      start: moment().subtract(4, 'weeks').startOf('day').toDate(),
      end: moment().endOf('day').toDate()
    })},
    { label: 'Month to date', value: 'mtd', getValue: () => ({
      start: moment().startOf('month').toDate(),
      end: moment().endOf('day').toDate()
    })},
    { label: 'Last 3 months', value: '3m', getValue: () => ({
      start: moment().subtract(3, 'months').startOf('day').toDate(),
      end: moment().endOf('day').toDate()
    })},
    { label: 'Last 6 months', value: '6m', getValue: () => ({
      start: moment().subtract(6, 'months').startOf('day').toDate(),
      end: moment().endOf('day').toDate()
    })},
    { label: 'Last 12 months', value: '12m', getValue: () => ({
      start: moment().subtract(12, 'months').startOf('day').toDate(),
      end: moment().endOf('day').toDate()
    })},
    { label: 'All time', value: 'all', getValue: () => ({
      start: moment('2020-01-01').toDate(),
      end: moment().endOf('day').toDate()
    })}
  ];

  const handleOptionSelect = (option) => {
    setSelectedOption(option.label);
    setDropdownOpen(false);
    const dateRange = option.getValue();
    onDateChange(dateRange, option.value);
  };

  const handleCustomRange = () => {
    setDropdownOpen(false);
    setCustomModalOpen(true);
    // Set default dates for custom range
    setCustomStartDate(moment().subtract(30, 'days').format('YYYY-MM-DD'));
    setCustomEndDate(moment().format('YYYY-MM-DD'));
  };

  const applyCustomRange = () => {
    if (customStartDate && customEndDate) {
      const start = moment(customStartDate).startOf('day').toDate();
      const end = moment(customEndDate).endOf('day').toDate();
      
      if (moment(end).isBefore(start)) {
        setModalDroppedAlert(true);
        return;
      }

      const label = `${moment(start).format('MMM D, YYYY')} - ${moment(end).format('MMM D, YYYY')}`;
      setSelectedOption(label);
      setCustomModalOpen(false);
      onDateChange({ start, end }, 'custom');
    }
  };

  const getIconForRange = () => {
    return 'fa fa-calendar';
  };

  return (
    <>
      <Dropdown isOpen={dropdownOpen} toggle={toggle} className="date-filter-dropdown btn-style-border">
        <DropdownToggle caret className="date-filter-toggle">
          <i className={`${getIconForRange()} mr-2`}></i>
          {selectedOption}
        </DropdownToggle>
        <DropdownMenu end className="date-filter-menu">
          <div className="dropdown-header">Select Date Range</div>
          {dateOptions.map((option, index) => (
            <DropdownItem 
              key={option.value}
              onClick={() => handleOptionSelect(option)}
              className={selectedOption === option.label ? 'active' : ''}
            >
              <span className="option-label">{option.label}</span>
              {selectedOption === option.label && (
                <i className="fa fa-check ms-auto text-primary"></i>
              )}
            </DropdownItem>
          ))}
          <DropdownItem divider />
          <DropdownItem onClick={handleCustomRange} className="custom-range-item">
            <i className="fa fa-calendar-alt mr-2"></i>
            <span className="option-label">Custom Range</span>
            <i className="fa fa-chevron-right ms-auto"></i>
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>

      {/* Custom Range Modal */}
      <Modal isOpen={customModalOpen} toggle={toggleCustomModal} className="custom-date-modal">
        <ModalHeader toggle={toggleCustomModal}>
          <i className="fa fa-calendar-alt mr-2"></i>
          Select Custom Date Range
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="startDate">Start Date</Label>
                <Input
                  type="date"
                  id="startDate"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  max={moment().format('YYYY-MM-DD')}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="endDate">End Date</Label>
                <Input
                  type="date"
                  id="endDate"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  max={moment().format('YYYY-MM-DD')}
                  min={customStartDate}
                />
              </FormGroup>
            </Col>
          </Row>
          <div className="date-preview mt-3">
            <small className="text-muted">Selected Range:</small>
            <div className="selected-range-text">
              {customStartDate && customEndDate ? (
                <>
                  {moment(customStartDate).format('MMM D, YYYY')} - {moment(customEndDate).format('MMM D, YYYY')}
                  <span className="ms-2 text-muted">
                    ({moment(customEndDate).diff(moment(customStartDate), 'days') + 1} days)
                  </span>
                </>
              ) : (
                'Please select both dates'
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleCustomModal}>
            Cancel
          </Button>
          <Button 
            color="primary" 
            onClick={applyCustomRange}
            disabled={!customStartDate || !customEndDate}
          >
            <i className="fa fa-check mr-2"></i>
            Apply Range
          </Button>
        </ModalFooter>
      </Modal>


      {/* --------------- Altert Dropped--------------- */}
         <Modal isOpen={modalDroppedAlert} toggle={() => setModalDroppedAlert(false)} className={`main-modal new-user-modal modal-dialog-centered`}>
            <ModalHeader toggle={() => setModalDroppedAlert(false)}></ModalHeader>
            <ModalBody>
               <div className='add-content'>
                  <h2>End date must be after start date</h2>
                  <div className='templates-inner'>
                     <div className='btn-group'>
                        <button className='btn-style-new' onClick={() => setModalDroppedAlert(false)}>Ok</button>
                     </div>
                  </div>
               </div>
            </ModalBody>
         </Modal>
    </>
  );
};

export default DateFilter;